// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rpsc/App/Components/Notify";
import { AddressValidationHandlerResult } from "@rpsc/App/Handlers/XNG/AddressValidation";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@rpsc/MobiusOverrides/Button/Button";
import AddressInfoDisplay from "@rpsc/Overrides/Components/AddressInfoDisplay";
import React, { useEffect } from "react";

export interface AddressValidationModalProps {
    isOpen: boolean;
    suggestedAddressResult?: AddressValidationHandlerResult;
    shipToAddress?: ShipToModel;
    title?: string;
    setIsOpen: (isOpen: boolean) => void;
    onEdit: () => void;
    onUseCurrentAddress: () => void;
    onAcceptSuggestion: (address?: any) => void;
    updateAddress?: (address: any) => void;
    // in case we need to avoid rerenders
    selector?: (state: CustomAppState) => ShipToModel;
}

export interface AddressValidationModalStyles {
    wrapper?: InjectableCss;
    recommendedAddress?: InjectableCss;
    shipToAddress?: InjectableCss;
    currentAddressActions?: InjectableCss;
    editLink?: LinkPresentationProps;
    useAddress?: ButtonPresentationProps;
    acceptButton?: ButtonPresentationProps;
    title?: TypographyPresentationProps;
    notify?: InjectableCss;
}

export const addressValidationModalStyles: AddressValidationModalStyles = {};
const AddressValidationModal = ({
    isOpen,
    suggestedAddressResult,
    shipToAddress: propShipToAddress,
    title = translate("Address Validation"),
    setIsOpen,
    onEdit,
    onUseCurrentAddress,
    onAcceptSuggestion,
    updateAddress,
    selector,
}: AddressValidationModalProps) => {
    const suggestedAddress = suggestedAddressResult?.addressResultDto;
    const meetsMinimumMailabilityScore = suggestedAddress?.meetsMinimumMailabilityScore;
    const styles = addressValidationModalStyles;

    const { countries, shipToAddress } = useAppSelector(state => {
        return {
            countries: getCurrentCountries(state),
            shipToAddress: propShipToAddress || selector?.(state as CustomAppState),
        };
    });

    const onAcceptHandler = () => {
        const { address1, address2, address3, address4, city, country, state, postalCode } =
            suggestedAddressResult?.addressResultDto || {};
        const selectedCountry = countries?.find(c => c.abbreviation === country);
        if (suggestedAddressResult?.addressResultDto && countries?.length) {
            updateAddress?.({
                ...(shipToAddress || {}),
                address1,
                address2,
                address3,
                address4,
                city,
                state: selectedCountry!.states!.find(({ abbreviation }) => abbreviation === state)!,
                country: selectedCountry!,
                postalCode,
            } as ShipToModel);
            setTimeout(
                () =>
                    onAcceptSuggestion({
                        ...(shipToAddress || {}),
                        address1,
                        address2,
                        address3,
                        address4,
                        city,
                        state: selectedCountry!.states!.find(({ abbreviation }) => abbreviation === state)!,
                        country: selectedCountry!,
                        postalCode,
                    } as ShipToModel),
                150,
            );
        }
        setIsOpen(false);
    };

    const currentAddressActions = (
        <StyledWrapper {...styles.currentAddressActions}>
            <Link
                {...styles.editLink}
                onClick={() => {
                    setIsOpen(false);
                    onEdit?.();
                }}
                //  XNG-Change
                data-test-selector="edit_address_validation_button"
            >
                {translate("Edit")}
            </Link>
            <Button
                {...styles.useAddress}
                onClick={() => {
                    setIsOpen(false);
                    onUseCurrentAddress();
                }}
                // XNG-Change
                data-test-selector="use_address_validation_button"
            >
                {translate("Use Address")}
            </Button>
        </StyledWrapper>
    );
    return (
        <Modal headline={title} isOpen={isOpen} isCloseable handleClose={() => setIsOpen(false)}>
            <Notify
                {...styles.notify}
                showClose={false}
                type={suggestedAddressResult?.errorMessage ? "danger" : "warning"}
            >
                {suggestedAddressResult?.errorMessage
                    ? siteMessage("Custom_AddressValidation_Failure", suggestedAddressResult.errorMessage)
                    : !meetsMinimumMailabilityScore
                    ? siteMessage("Custom_AddressValidation_NoRecommendation")
                    : siteMessage("Custom_AddressValidation_RecommendationConfirm")}
            </Notify>
            <StyledWrapper {...styles.wrapper}>
                {meetsMinimumMailabilityScore && (
                    <StyledWrapper {...styles.recommendedAddress}>
                        <Typography {...styles.title}>{translate("We Recommend")}</Typography>
                        <AddressInfoDisplay {...(suggestedAddress as any)} />
                        {/* XNG-Change */}
                        <Button
                            {...styles.acceptButton}
                            onClick={onAcceptHandler}
                            data-test-selector="accept_address_validation_button"
                        >
                            {translate("Accept")}
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.shipToAddress}>
                    <Typography {...styles.title}>{translate("You Entered")}</Typography>
                    <AddressInfoDisplay
                        {...(shipToAddress as any)}
                        state={shipToAddress?.state ? shipToAddress.state.abbreviation : undefined}
                        country={shipToAddress?.country ? shipToAddress.country.abbreviation : undefined}
                        firstName=""
                        lastName=""
                        companyName=""
                        email=""
                        phone=""
                        attention=""
                        fax=""
                    />
                    {meetsMinimumMailabilityScore && currentAddressActions}
                </StyledWrapper>
            </StyledWrapper>
            {!meetsMinimumMailabilityScore && currentAddressActions}
        </Modal>
    );
};

export default AddressValidationModal;
